import { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import logger from '@widgets/logger';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import ComboLocationSelect, { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import { KB } from '@constants/file';
import { CHANNEL } from '@constants/channels';
import { useTranslation, useThrottle, useCopyToClipboard, useLocations, useChannels } from '@hooks';
import { micrositesService } from '@services';
import { micrositesActions } from '@actions/microsites';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CustomDomainSection from './CustomDomain/CustomDomainSection';
import { StyledSwitch } from './styles';

const StyledSlugInput = styled(Input)({
    '& .MuiInputLabel-root': {
        transform: 'translate(278px, 12px)',
    },
    '& .MuiOutlinedInput-notchedOutline legend span': {
        padding: 0,
    },
    '& .MuiInputBase-adornedStart fieldset legend': {
        padding: '0 7px',
    },
});

const CssStartAdornment = styled(InputAdornment)({
    margin: 0,
    '& .MuiTypography-root': {
        fontSize: '14px',
    },
});

const General = ({ isView, selectedMicrosite, brands }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [customDomainHostname, setCustomDomainHostname] = useState(
        selectedMicrosite?.customDomainStatus === 1 ? selectedMicrosite.customDomainHostname : null,
    );

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const { onCopy } = useCopyToClipboard();
    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);
    const { getSelectLocationsOptions } = useLocations();
    const { getAccessibleChannelIds, getChannelOptions } = useChannels();

    const locationType = watch('locationType');
    const locationId = watch('locationId');
    const brandId = watch('brandId');
    const slug = watch('slug');
    const logoUrl = watch('logoUrl');
    const showLiveChat = watch('showLiveChat');

    useEffect(() => {
        if (locationType === LOCATION_TYPE.LOCATION && locationId) {
            dispatch(micrositesActions.getLocationReviews(locationId));
        } else if (locationType === LOCATION_TYPE.BRAND && brandId) {
            const brandLocationId = brands?.find(b => b.id === brandId)?.locations?.[0]?.id;
            if (brandLocationId) {
                dispatch(micrositesActions.getLocationReviews(brandLocationId));
            }
        }
    }, [locationId, brandId, locationType]);

    const brandsOptions = useMemo(() => (brands || []).map(item => ({ value: item.id, label: item.title })), [brands]);

    const companyOptions = useMemo(() => getSelectLocationsOptions(), []);

    const channelsOptions = useMemo(() => {
        const filterCb = c => c.type === CHANNEL.LIVE_CHAT;
        return getChannelOptions({ in: true, out: true }, getAccessibleChannelIds(filterCb));
    }, [locationId, brandId, locationType]);

    const uploadLogo = async e => {
        try {
            setIsUploading(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await micrositesService.uploadMicrositeImage(data, selectedMicrosite.id);
            setValue('logoUrl', response.publicUrl, { shouldDirty: true });
        } catch (_) {
            logger.error("Couldn't upload logo microsite");
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const onDeleteLogo = () => setValue('logoUrl', null, { shouldDirty: true });

    return (
        <>
            <div style={{ margin: '8px 0' }}>
                <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('MicrositesPage.codeName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isView}
                            sx={{ marginBottom: '8px' }}
                        />
                    )}
                />
                <ComboLocationSelect
                    brandsOptions={brandsOptions}
                    locationsOptions={companyOptions}
                    module="microsites"
                    data={selectedMicrosite}
                    disabled={isView}
                    isView={isView}
                    updatedLocationCb={value => {
                        dispatch(micrositesActions.getLocationReviews(value));
                    }}
                    updatedLocationTypeCb={() => {
                        setValue('showLiveChat', false, { shouldDirty: true });
                        setValue('liveChatChannelId', '', { shouldDirty: true, shouldValidate: true });
                    }}
                />
                <Controller
                    control={control}
                    name="slug"
                    render={({ field, fieldState: { error }, ref }) => {
                        const urlDomain = customDomainHostname || import.meta.env.REACT_APP_SURVEY_DOMAIN;
                        const fullUrl = `${customDomainHostname ? 'https://' : ''}${urlDomain}/${slug}`;
                        return (
                            <StyledSlugInput
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.publicLink')}
                                startAdornment={
                                    <CssStartAdornment position="start">{`${urlDomain}/`}</CssStartAdornment>
                                }
                                onAddonClick={() => onCopyClick(fullUrl)}
                                addonComponent={
                                    <ContentCopyIcon sx={{ fontSize: 18, color: 'var(--text-secondary)' }} />
                                }
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                                disabled={isView}
                            />
                        );
                    }}
                />
                <CustomDomainSection
                    customDomainId={selectedMicrosite?.customDomainId}
                    isView={isView}
                    onDomainChange={domainId => {
                        setValue('customDomainId', domainId, { shouldDirty: true });
                    }}
                    onHostnameChange={setCustomDomainHostname}
                />
            </div>
            <Typography variant="subtitle1">{t('common.logo')}</Typography>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', margin: '4px 0 8px' }}>
                {t('common.imageUploadFormat', { size: '500KB' })}
            </Typography>
            <ImageUploaderSingle
                maxSize={KB * 500}
                minSize={10 * KB}
                extensions="image/*"
                cropConfig={{ aspectRatio: 1, outputWidth: 250 }}
                logoUrl={logoUrl}
                disabled={isUploading || isView}
                onDelete={onDeleteLogo}
                uploadLogo={uploadLogo}
                isUploading={isUploading}
            />
            <Typography variant="subtitle1" sx={{ paddingTop: '32px', color: 'var(--text-primary)' }}>
                {t('MicrositesPage.additionalSettings')}
            </Typography>
            <Controller
                control={control}
                name="socialMediaEnable"
                render={({ field, ref }) => (
                    <StyledSwitch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('socialMediaEnable', event.target.checked, { shouldDirty: true });
                        }}
                        label={t('MicrositesPage.displaySocialMediaLinks')}
                        disabled={isView}
                    />
                )}
            />
            <Controller
                control={control}
                name="showReviews"
                render={({ field, ref }) => (
                    <StyledSwitch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('showReviews', event.target.checked, { shouldDirty: true });
                        }}
                        label={t('MicrositesPage.showReviews')}
                        disabled={isView}
                    />
                )}
            />
            <Controller
                control={control}
                name="showLiveChat"
                render={({ field, ref }) => (
                    <StyledSwitch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('showLiveChat', event.target.checked, { shouldDirty: true, shouldValidate: true });
                            if (!event.target.checked) {
                                setValue('liveChatChannelId', '', { shouldDirty: true, shouldValidate: true });
                            }
                        }}
                        label={t('MicrositesPage.showLiveChat')}
                        disabled={isView}
                    />
                )}
            />
            {showLiveChat && (
                <Controller
                    control={control}
                    name="liveChatChannelId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            onChange={({ target: { value } }) => {
                                setValue('liveChatChannelId', value, { shouldValidate: true, shouldDirty: true });
                            }}
                            ref={ref}
                            label={t('common.channel')}
                            fullWidth
                            options={channelsOptions}
                            required
                            style={{ marginTop: '8px' }}
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
            )}
        </>
    );
};

export default General;
